import type {
  ProgressChart,
  ProgressOutcomes,
  ProgressResponse,
  ProgressSideEffects,
  ProgressThankYou,
} from "@/domains/progress/models/types";

/** Payload order — goals, symptom groups, quality of life — and it is never re-sorted. */
export const collectProgressCharts = (progress: ProgressResponse): ProgressChart[] => {
  return [
    ...progress.goals,
    ...progress.symptomGroups,
    ...(progress.qualityOfLife === null ? [] : [progress.qualityOfLife]),
  ];
};

/** A count of zero is a real answer and stays; zero check-ins answered means never asked. */
export const selectProgressOutcomeItems = (outcomes: ProgressOutcomes) => {
  if (!outcomes.applicable) {
    return [];
  }

  return outcomes.items.filter((item) => {
    return item.checkInsAnswered > 0;
  });
};

export const selectProgressSideEffectEntries = (sideEffects: ProgressSideEffects) => {
  return sideEffects.applicable ? [...sideEffects.entries] : [];
};

export const collectProgressIdealLines = (charts: ProgressChart[]) => {
  return charts.flatMap((chart) => {
    return chart.idealLine === null ? [] : [chart.idealLine];
  });
};

export const findIdealLineExplainerIndex = (charts: ProgressChart[]) => {
  return charts.findIndex((chart) => {
    return chart.idealLine !== null;
  });
};

export const findFirstPointNoteIndex = (charts: ProgressChart[]) => {
  const idealLineExplainerIndex = findIdealLineExplainerIndex(charts);

  return idealLineExplainerIndex === -1 ? 0 : idealLineExplainerIndex;
};

export const selectProgressThankYouMessage = (thankYou: ProgressThankYou) => {
  if (!thankYou.applicable || thankYou.message === null) {
    return null;
  }

  const message = thankYou.message.trim();

  return message.length > 0 ? message : null;
};

/** A figure with no note, or no figure, is half a claim: the tile is dropped rather than part-rendered. */
export const selectProgressPotentialDiscount = (thankYou: ProgressThankYou) => {
  const potentialDiscount = thankYou.potentialDiscount ?? null;

  if (potentialDiscount === null || potentialDiscount.note.trim().length === 0) {
    return null;
  }

  return potentialDiscount.amountUsd > 0 ? potentialDiscount : null;
};
